import { useStopLists } from "@/api/stop-list.api";
import { Button } from "@/components/generic/Button";
import { TagField } from "@/components/generic/TagField";
import { StopList } from "@/types/campaign";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown, X } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import { SegmentHeader } from "../campaign/basic/SegmentHeader";

type Props = {
  currentStopLists: StopList[];
  onSubmit: (stopListIds: string[]) => void;
  availableStopLists: StopList[];
  isCreating?: boolean;
};

const schema = z.object({
  stopLists: z.array(z.string()).optional(),
});

type FormData = z.infer<typeof schema>;

export function CampaignStopWindowDropdown({
  currentStopLists,
  onSubmit,
  availableStopLists,
  isCreating = false,
}: Props) {
  const { data: stopLists } = useStopLists();

  const { control, handleSubmit, watch, setValue, reset } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      stopLists: [],
    },
  });

  const disabledKeys = useMemo(() => {
    const keys = new Set<string>();
    availableStopLists.forEach((sl) => {
      if (
        (sl.mandatory === true || sl.campaignList === true) &&
        sl.hidden !== true
      ) {
        keys.add(sl.uniqueId);
      }
    });
    return keys;
  }, [availableStopLists]);

  useEffect(() => {
    if (availableStopLists.length > 0) {
      const alwaysEnabledStopLists = availableStopLists
        .filter(
          (sl) =>
            (sl.mandatory === true || sl.campaignList === true) &&
            sl.hidden !== true,
        )
        .map((sl) => sl.uniqueId);

      const existingStopLists =
        currentStopLists
          ?.filter((item) => item && item.hidden !== true)
          .map((sl) => sl.uniqueId)
          .filter(Boolean) || [];

      const combined = [
        ...new Set([...existingStopLists, ...alwaysEnabledStopLists]),
      ];

      reset({
        stopLists: combined,
      });
    }
  }, [availableStopLists, currentStopLists, reset]);

  const selectedStopLists = watch("stopLists") || [];

  const handleFormSubmit = (data: FormData) => {
    onSubmit(data.stopLists || []);
  };

  const removeStopList = (id: string) => {
    if (!disabledKeys.has(id)) {
      const newValues = selectedStopLists.filter((slId) => slId !== id);
      setValue("stopLists", newValues);
    }
  };

  const getStopListById = (id: string): StopList | undefined => {
    const stopList = availableStopLists.find((sl) => sl.uniqueId === id);
    if (stopList) return stopList;

    if (currentStopLists && isCreating == false) {
      const campaignStopList = currentStopLists.find(
        (sl) => sl && sl.uniqueId === id,
      );
      if (campaignStopList) return campaignStopList;
    } else {
      const campaignStopList = stopLists?.find((sl) => sl.uniqueId === id);
      if (campaignStopList) return campaignStopList;
    }

    return undefined;
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="flex w-full justify-center">
        <div className="max-w-xl">
          <SegmentHeader
            title="Stoplister"
            description="Med stoplister kan du ekskludere bestemte virksomheder fra dine kampagner. Vælg de stoplister, som du ønsker at anvende i denne kampagne."
          />

          <div className="mt-9 flex flex-col gap-10 px-14">
            <Controller
              control={control}
              name="stopLists"
              render={({ field, fieldState: { error } }) => (
                <TagField
                  disabledKeys={disabledKeys}
                  trailingIcon={<ChevronDown className="size-3.5 xl:size-5" />}
                  placeholder="Vælg stoplister"
                  aria-label="Stoplister"
                  selectedOptions={
                    field.value?.map((id) => {
                      const stopList = getStopListById(id);
                      const isGloballyEnabled = disabledKeys.has(id);

                      return {
                        label: stopList?.name || "Ukendt",
                        value: id,
                        count: isGloballyEnabled
                          ? "Altid aktiveret"
                          : stopList?.count || 0,
                      };
                    }) || []
                  }
                  setSelectedOptions={(value) => {
                    if (Array.isArray(value)) {
                      field.onChange(value.map((item) => item.value));
                    }
                  }}
                  options={availableStopLists
                    .filter((item) => item.hidden !== true)
                    .map((stopList) => {
                      const isGloballyEnabled = disabledKeys.has(
                        stopList.uniqueId,
                      );

                      return {
                        label: stopList.name,
                        value: stopList.uniqueId,
                        count: isGloballyEnabled
                          ? "Altid aktiveret"
                          : stopList.uniqueId === "virtual-all-leads"
                            ? ""
                            : stopList.count || 0,
                      };
                    })}
                  errorMessage={error?.message}
                  className="w-full"
                />
              )}
            />
            {selectedStopLists.length > 0 && (
              <div>
                <ul className="flex flex-col gap-y-2">
                  {(() => {
                    const validItems = selectedStopLists.filter((id) => {
                      const stopList = getStopListById(id);
                      return stopList && stopList.hidden !== true;
                    });

                    const virtualItem = validItems.find(
                      (id) => id === "virtual-all-leads",
                    );
                    const otherItems = validItems.filter(
                      (id) => id !== "virtual-all-leads",
                    );
                    const orderedItems = virtualItem
                      ? [virtualItem, ...otherItems.reverse()]
                      : otherItems.reverse();

                    return orderedItems.map((id) => {
                      const stopList = getStopListById(id);
                      const isGloballyEnabled = disabledKeys.has(id);

                      return (
                        <li
                          key={id}
                          className="border-theme-outline-variant flex items-center justify-between rounded-lg border px-6 py-4"
                        >
                          <div className="flex items-center gap-4">
                            <span className="text-[11px] font-bold">
                              {stopList?.name || `Ukendt (ID: ${id})`}
                            </span>
                            <span className="text-[11px]">
                              {stopList?.uniqueId == "virtual-all-leads"
                                ? ""
                                : stopList?.count || 0}
                            </span>
                          </div>

                          {isGloballyEnabled ? (
                            <span className="text-theme-on-background-variant flex items-center gap-2 text-xs">
                              Altid aktiveret
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => removeStopList(id)}
                              className="cursor-pointer"
                            >
                              <X size={20} className="text-theme-on-glass" />
                            </button>
                          )}
                        </li>
                      );
                    });
                  })()}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-auto flex justify-center gap-4 pt-12">
        <Button size="lg" type="submit" animate>
          Gem
        </Button>
      </div>
    </Form>
  );
}
